import { useState } from "react";
import { Layout } from "@jambushrushti/components/layout/Layout";
import { PageBreadcrumb } from "@jambushrushti/components/ui/page-breadcrumb";
import { Card, CardContent } from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@jambushrushti/components/ui/select";
import { Download, FileText, Calendar, BookOpen, Loader2 } from "lucide-react";
import {
  useEmagazinesByYear,
  Emagazine,
} from "@jambushrushti/hooks/useEmagazines";
import { format } from "date-fns";

export default function EmagazinePage() {
  const { data: magazinesByYear, isLoading } = useEmagazinesByYear();
  const [selectedYear, setSelectedYear] = useState<string>("all");

  const years = magazinesByYear
    ? Object.keys(magazinesByYear).sort((a, b) => Number(b) - Number(a))
    : [];

  const filteredMagazines = () => {
    if (!magazinesByYear) {
      return [];
    }
    if (selectedYear === "all") {
      return Object.values(magazinesByYear).flat();
    }
    return magazinesByYear[Number(selectedYear)] || [];
  };

  const magazines = filteredMagazines();

  return (
    <Layout>
      <div className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-primary/10 via-background to-primary/5 py-16 lg:py-20">
          <div className="container mx-auto max-w-7xl px-4">
            <PageBreadcrumb
              items={[
                { label: "Home", href: "/" },
                { label: "eMagazine – Jambu Jyoti", href: "/emagazine" },
              ]}
            />
            <div className="mx-auto mt-8 max-w-3xl text-center">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-2 text-sm font-medium text-primary">
                <BookOpen className="h-4 w-4" />
                Digital Publication
              </div>
              <h1 className="font-display mb-4 text-4xl font-bold text-foreground md:text-5xl">
                eMagazine – Jambu Jyoti
              </h1>
              <p className="text-lg text-muted-foreground">
                Explore our digital magazine collection featuring insights,
                articles, and teachings from the rich tradition of Jain
                scholarship and Gurudev's wisdom.
              </p>
            </div>
          </div>
        </section>

        {/* Archive Section */}
        <section className="py-12 lg:py-16">
          <div className="container mx-auto max-w-7xl px-4">
            {/* Filters */}
            <div className="mb-8 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">
                  Magazine Archive
                </h2>
                <p className="text-muted-foreground">
                  Browse and download past issues
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  Filter by year:
                </span>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Select Year" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Years</SelectItem>
                    {years.map(year => (
                      <SelectItem key={year} value={year}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Magazine Grid */}
            {isLoading ? (
              <div className="flex items-center justify-center py-16">
                <Loader2 className="h-10 w-10 animate-spin text-primary" />
              </div>
            ) : magazines.length > 0 ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {magazines.map(magazine => (
                  <MagazineCard key={magazine.id} magazine={magazine} />
                ))}
              </div>
            ) : (
              <div className="py-16 text-center">
                <FileText className="mx-auto mb-4 h-16 w-16 text-muted-foreground/50" />
                <h3 className="mb-2 text-xl font-semibold text-foreground">
                  No Issues Available
                </h3>
                <p className="text-muted-foreground">
                  {selectedYear === "all"
                    ? "Magazine issues will be available soon."
                    : `No issues found for ${selectedYear}.`}
                </p>
              </div>
            )}

            {/* Year-wise Archive (when showing all) */}
            {selectedYear === "all" &&
              magazinesByYear &&
              Object.keys(magazinesByYear).length > 0 && (
                <div className="mt-16 space-y-12">
                  <h2 className="font-display text-center text-2xl font-bold text-foreground">
                    Browse by Year
                  </h2>
                  {years.map(year => (
                    <div key={year} className="space-y-4">
                      <div className="flex items-center gap-3">
                        <Calendar className="h-5 w-5 text-primary" />
                        <h3 className="text-xl font-semibold text-foreground">
                          {year}
                        </h3>
                        <span className="text-sm text-muted-foreground">
                          ({magazinesByYear[Number(year)].length} issue
                          {magazinesByYear[Number(year)].length !== 1
                            ? "s"
                            : ""}
                          )
                        </span>
                      </div>
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {magazinesByYear[Number(year)].map(magazine => (
                          <MagazineCard
                            key={magazine.id}
                            magazine={magazine}
                            compact
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function MagazineCard({
  magazine,
  compact = false,
}: {
  magazine: Emagazine;
  compact?: boolean;
}) {
  return (
    <Card className="group overflow-hidden transition-all duration-300 hover:shadow-lg">
      <div className="relative aspect-[3/4] overflow-hidden bg-gradient-to-br from-primary/20 to-primary/5">
        {magazine.cover_image ? (
          <img
            src={magazine.cover_image}
            alt={magazine.title}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center text-primary/60">
            <FileText className="mb-2 h-16 w-16" />
            <span className="text-sm font-medium">Jambu Jyoti</span>
          </div>
        )}
        <div className="absolute right-3 top-3 rounded bg-primary px-2 py-1 text-xs font-medium text-primary-foreground">
          {magazine.year}
        </div>
      </div>
      <CardContent className={compact ? "p-3" : "p-4"}>
        <h3
          className={`line-clamp-2 font-semibold text-foreground ${compact ? "text-sm" : "text-base"} mb-1`}
        >
          {magazine.title}
        </h3>
        {magazine.volume && (
          <p className="mb-1 text-xs text-muted-foreground">
            {magazine.volume}
          </p>
        )}
        {magazine.issue_date && (
          <p className="mb-3 text-xs text-muted-foreground">
            {format(new Date(magazine.issue_date), "MMMM yyyy")}
          </p>
        )}
        {!compact && magazine.description && (
          <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
            {magazine.description}
          </p>
        )}
        <Button
          asChild
          size={compact ? "sm" : "default"}
          className="w-full gap-2"
        >
          <a href={magazine.pdf_url} target="_blank" rel="noopener noreferrer">
            <Download className="h-4 w-4" />
            Download PDF
          </a>
        </Button>
      </CardContent>
    </Card>
  );
}
